import { useQuery } from "@tanstack/react-query";

import { listRoles, type Permission } from "@/api/admin-api";
import type { Role } from "@/store/auth-store";

import { ALL_PERMISSIONS, ALL_ROLES, ROLE_META } from "./lookups";

const ROLES_KEY = ["admin", "roles"] as const;

export function PermissionsMatrix() {
  const rolesQuery = useQuery({
    queryKey: ROLES_KEY,
    queryFn: () => listRoles(),
    // Roles are static at runtime; no need to revalidate aggressively.
    staleTime: 5 * 60 * 1000,
  });

  const map = buildLookup(rolesQuery.data ?? []);
  const bySetting = buildSettingLookup(rolesQuery.data ?? []);
  const anyBySetting = [...bySetting.values()].some((set) => set.size > 0);

  return (
    <section data-cy="permissions-matrix-section" className="space-y-3">
      <header>
        <h2 className="text-base font-semibold text-foreground-heading">
          Role permissions
        </h2>
        <p className="text-xs text-foreground-muted">
          What each role can do RIGHT NOW. A tick is granted by the role
          itself (<code className="rounded bg-table-row-alt px-1">app/auth/roles.py</code>,
          changed only by a release); a dot is granted by an organisation
          setting and goes away if that setting is switched off.
        </p>
      </header>

      <div className="overflow-auto rounded-xl border border-border bg-background-card shadow-theme-sm">
        <table className="w-full text-sm" data-cy="permissions-matrix">
          <thead>
            <tr className="border-b-2 border-border-strong bg-table-header text-left text-[12px] font-semibold uppercase tracking-[0.05em] text-table-header-text">
              <th className="sticky left-0 bg-table-header px-4 py-3">Permission</th>
              {ALL_ROLES.map((r) => (
                <th key={r} className="px-3 py-3 text-center">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium ${ROLE_META[r].badge}`}
                  >
                    {ROLE_META[r].label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ALL_PERMISSIONS.map((p, idx) => {
              const baseRowBg =
                idx % 2 === 0 ? "bg-table-row-bg" : "bg-table-row-alt";
              return (
                <tr
                  key={p.key}
                  className={`border-t border-table-border ${baseRowBg}`}
                  data-cy="permission-row"
                  data-permission={p.key}
                >
                  <td
                    className={`sticky left-0 px-4 py-2.5 text-xs text-foreground ${baseRowBg}`}
                  >
                    {p.label}
                    <div className="font-mono text-[10px] text-foreground-muted">
                      {p.key}
                    </div>
                  </td>
                  {ALL_ROLES.map((r) => {
                    const byRole = map.get(r)?.has(p.key) ?? false;
                    // A setting-granted permission is REAL — the holder can
                    // do it today — so it must not render as denied. It is
                    // marked differently because the reason matters: an
                    // admin auditing "who can sign a report" needs to know
                    // this one hangs off a toggle.
                    const bySettingHere = bySetting.get(r)?.has(p.key) ?? false;
                    const has = byRole || bySettingHere;
                    return (
                      <td
                        key={r}
                        className="px-3 py-2.5 text-center"
                        data-cy={`cell-${r}-${p.key}`}
                        data-has={has ? "true" : "false"}
                        data-source={
                          byRole ? "role" : bySettingHere ? "setting" : "none"
                        }
                      >
                        {bySettingHere && !byRole ? (
                          <span
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-accent-light text-accent"
                            aria-label="granted by an organisation setting"
                            title="Granted by Settings → System settings → Workflow → Allow sonographers to sign. Switching that off removes it."
                          >
                            ●
                          </span>
                        ) : has ? (
                          <span
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-status-done-bg text-status-done"
                            aria-label="granted"
                          >
                            ✓
                          </span>
                        ) : (
                          <span
                            className="inline-flex h-6 w-6 items-center justify-center text-foreground-muted"
                            aria-label="not granted"
                          >
                            –
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend only when something on this screen is actually held by a
          setting — on a deployment where the toggle is off, explaining a
          marker nobody can see is noise. */}
      {anyBySetting ? (
        <p
          className="flex items-center gap-2 text-xs text-foreground-muted"
          data-cy="matrix-setting-legend"
        >
          <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-light text-[11px] text-accent">
            ●
          </span>
          Granted by an organisation setting, not by the role. Sonographers
          can draft and sign because <strong>Settings → System settings →
          Workflow → &ldquo;Allow sonographers to sign&rdquo;</strong> is on;
          turning it off removes both.
        </p>
      ) : null}

      {rolesQuery.isLoading ? (
        <p className="text-xs text-foreground-muted" data-cy="matrix-loading">
          Loading role definitions…
        </p>
      ) : null}
      {rolesQuery.error ? (
        <p className="text-xs text-status-urgent" data-cy="matrix-error">
          Could not load role definitions. The matrix above shows the static
          frontend mirror.
        </p>
      ) : null}
    </section>
  );
}

function buildLookup(
  rows: { role: Role; permissions: Permission[] }[],
): Map<Role, Set<Permission>> {
  const m = new Map<Role, Set<Permission>>();
  for (const row of rows) m.set(row.role, new Set(row.permissions));
  return m;
}

/** Permissions each role holds because a SETTING grants them, not the
 *  role matrix. Kept apart from ``buildLookup`` so the cell can say which
 *  of the two is responsible. */
function buildSettingLookup(
  rows: { role: Role; granted_by_setting?: Permission[] }[],
): Map<Role, Set<Permission>> {
  const m = new Map<Role, Set<Permission>>();
  for (const row of rows) m.set(row.role, new Set(row.granted_by_setting ?? []));
  return m;
}
